import React from 'react'

const TermsPage: React.FC = () => {
  return (
    <div className="bg-gray-950 text-white min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-8 gradient-text">Terms of Service</h1>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">1. Acceptance of Terms</h2>
            <p>
              By accessing or using VisSynth, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our service.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">2. Description of Service</h2>
            <p className="mb-4">
              VisSynth is an AI-powered video generation platform that enables users to create short-form videos for TikTok, YouTube Shorts, Instagram Reels, and other social platforms. Our service includes:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>AI-generated video scripts and storyboards</li>
              <li>Automated image and video generation</li>
              <li>Text-to-speech voiceover capabilities</li>
              <li>Video editing and customization tools</li>
              <li>Social media publishing integration</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">
              The service is provided "as is" and we reserve the right to modify, suspend, or discontinue any aspect of the service at any time without prior notice.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">3. User Accounts and Responsibilities</h2>
            <p className="mb-4">To use VisSynth, you must create an account and agree to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, complete, and current information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use</li>
              <li>Comply with these Terms of Service</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">4. Acceptable Use Policy</h2>
            <p className="mb-4">You agree NOT to use VisSynth to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Create content that is illegal, harmful, threatening, abusive, or defamatory</li>
              <li>Violate intellectual property rights of others</li>
              <li>Generate misleading, false, or deceptive content</li>
              <li>Produce adult content, hate speech, or violent material</li>
              <li>Spam or harass other users or platforms</li>
              <li>Attempt to circumvent usage limits or restrictions</li>
              <li>Reverse engineer or attempt to extract our AI models</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">
              Violation of this policy may result in immediate termination of your account.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">5. Intellectual Property and Content Ownership</h2>
            <p className="mb-4">
              <strong>Content Ownership:</strong> You retain ownership of all videos and content you create using VisSynth, subject to the following licenses:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>You grant us a license to use your content solely to provide the service</li>
              <li>You represent that you have all necessary rights to the content you create</li>
              <li>You are solely responsible for the legality of your generated content</li>
            </ul>
            <p className="mb-4 mt-4">
              <strong>Platform IP:</strong> VisSynth, including its technology, design, and brand, is protected by intellectual property laws. You may not:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Copy, modify, or distribute our software or algorithms</li>
              <li>Use our trademarks without explicit permission</li>
              <li>Scrape or extract data from our platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">6. Payment and Subscription Terms</h2>
            <p className="mb-4">
              VisSynth offers various subscription plans with different features and usage limits:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Payment is processed through secure third-party providers</li>
              <li>Subscriptions automatically renew unless cancelled</li>
              <li>Refunds are handled on a case-by-case basis</li>
              <li>We reserve the right to change pricing with 30 days notice</li>
              <li>Usage limits apply based on your selected plan</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">7. Third-Party Integrations</h2>
            <p className="mb-4">
              VisSynth integrates with various third-party platforms. By using these integrations, you agree to comply with their respective terms of service:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>TikTok:</strong> TikTok Terms of Service and Community Guidelines</li>
              <li><strong>YouTube:</strong> YouTube Terms of Service and API Policies</li>
              <li><strong>Instagram:</strong> Meta Terms of Service</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">
              You are responsible for ensuring your content complies with each platform's guidelines.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">8. Disclaimer of Warranties</h2>
            <p>
              VisSynth is provided "as is" without any warranties, express or implied. We do not guarantee that the service will:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Meet your specific requirements or expectations</li>
              <li>Be uninterrupted, timely, secure, or error-free</li>
              <li>Produce content that is acceptable on all platforms</li>
              <li>Be free from viruses or other harmful components</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">9. Limitation of Liability</h2>
            <p>
              VisSynth and its creators, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Your use or inability to use the service</li>
              <li>Content created or published using our platform</li>
              <li>Account termination or suspension</li>
              <li>Third-party platform actions against your content</li>
            </ul>
            <p className="text-sm text-gray-400 mt-2">
              In no event shall our total liability exceed the amount you paid for the service in the past 12 months.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">10. Account Termination</h2>
            <p className="mb-4">
              We reserve the right to suspend or terminate your account at any time, without notice, for:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violation of these Terms of Service</li>
              <li>Abuse of the service or its features</li>
              <li>Fraudulent or illegal activities</li>
              <li>Breach of intellectual property rights</li>
              <li>Any reason at our sole discretion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">11. Privacy and Data Protection</h2>
            <p>
              Your privacy is important to us. Please review our Privacy Policy to understand how we collect, use, and protect your personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">12. Modifications to Terms</h2>
            <p>
              We reserve the right to modify these Terms of Service at any time. We will notify users of material changes via email or through our website. Continued use of the service after changes constitutes acceptance of the new terms.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">13. Governing Law</h2>
            <p>
              These Terms of Service shall be governed by and construed in accordance with applicable laws. Any disputes arising under these terms shall be resolved through binding arbitration.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">14. Contact Information</h2>
            <p>
              If you have questions about these Terms of Service, please contact us through our support channel or via email.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold mb-4 text-cyan-400">15. General Provisions</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>These terms constitute the entire agreement between you and VisSynth</li>
              <li>Our failure to enforce any right does not constitute a waiver of that right</li>
              <li>If any provision is found unenforceable, the remaining provisions remain in full force</li>
              <li>We may assign these terms without restriction</li>
            </ul>
          </section>

          <section className="text-sm text-gray-400 mt-12 pt-8 border-t border-gray-800">
            <p>Last updated: December 2025</p>
            <p className="mt-2">By using VisSynth, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.</p>
          </section>
        </div>
      </div>
    </div>
  )
}

export default TermsPage
